import React, { useState, useEffect } from "react";
import Modal from "../Elements/Modal/Modal";
import MainButton from "../Elements/MainButton/MainButton";
import SecondButton from "../Elements/SecondButton/SecondButton";
import MainInput from "../Elements/MainInput/MainInput";
import ErrorMessage from "../Elements/ErrorMessage/ErrorMessage";
import SuccessMessage from "../Elements/SuccessMessage/SuccessMessage";
import { updateUser } from "../../services/UserService";
import "./AdminModalForm.css";

// Admin modal for editing basic user data (username, password, role)
const AdminEditUserModal = ({ isOpen, onClose, user, roles, onUpdated }) => {
  // Local form state
  const [formData, setFormData] = useState({
    username: "",
    new_password: "",
    role_id: "",
  });

  // UI state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Initialize form fields when modal opens or user changes
  useEffect(() => {
    if (isOpen && user) {
      setFormData({
        username: user.username || "",
        new_password: "",
        role_id: user.role_id?.toString() || "",
      });
      setError("");
      setSuccess("");
    }
  }, [isOpen, user]);

  // Handle controlled inputs change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError("");
  };

  // Build payload for updateUser API
  const buildPayload = () => ({
    email: user.email,
    username: formData.username,
    new_password: formData.new_password || null,
    role_id: Number(formData.role_id),
  });

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    try {
      setSaving(true);

      const payload = buildPayload();
      await updateUser({ userData: payload });

      setSuccess("User updated successfully.");
      // onUpdated is not called here in original code – left as-is
    } catch (err) {
      setError(err.message || "Failed to update user");
    } finally {
      setSaving(false);
    }
  };

  // Do not render if closed or user is missing
  if (!isOpen || !user) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={saving ? () => {} : onClose}
      title={`Edit User: ${user.email}`}
      actions={
        <>
          <SecondButton onClick={onClose} disabled={saving}>
            Cancel
          </SecondButton>
          <MainButton onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </MainButton>
        </>
      }
    >
      {/* User edit form */}
      <form onSubmit={handleSubmit} className="admin-modal-form">
        {/* Username field */}
        <label>
          Username
          <MainInput type="text" value={formData.username} onChange={handleChange} name="username" disabled={saving} />
        </label>

        {/* Optional new password */}
        <label>
          New Password (optional)
          <MainInput
            type="password"
            value={formData.new_password}
            onChange={handleChange}
            name="new_password"
            disabled={saving}
            placeholder="Leave empty to keep current password"
          />
        </label>

        {/* Role selector */}
        <label>
          Role
          <div className="admin-modal-select-wrapper">
            <select
              name="role_id"
              value={formData.role_id}
              onChange={handleChange}
              disabled={saving}
              className="admin-modal-select admin-modal-select-with-arrow"
            >
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
          </div>
        </label>

        {/* Error feedback */}
        {error && (
          <div className="admin-modal-error-wrapper">
            <ErrorMessage message={error} onClose={() => setError("")} />
          </div>
        )}

        {/* Success feedback */}
        {success && (
          <div className="admin-modal-success-wrapper">
            <SuccessMessage message={success} onClose={() => setSuccess("")} />
          </div>
        )}
      </form>
    </Modal>
  );
};

export default AdminEditUserModal;
